import { useContext, useRef, useState } from 'react'
import { observer } from 'mobx-react'
import { Button, Icon } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import type { GroupItem, GroupQuestion, ScalarAnswerValue } from '@/types/dataCollection'
import { ScalarQuestionField } from './ScalarQuestionField'
import {
  buildItemInitialValues,
  groupFieldErrorKey,
  groupMaxItems,
  groupMinItems,
  isQuestionVisible,
  resolveLabel,
} from './helpers'

interface GroupFieldProps {
  question: GroupQuestion
  items: GroupItem[]
  errors: Record<string, string>
  onItemsChange: (items: GroupItem[]) => void
  /** Marks an error key (the group id, or `group.index.inner`) as touched so its error may show. */
  onTouched: (key: string) => void
  addFallback: string
  removeLabel: string
}

export const GroupField = observer(
  ({
    question,
    items,
    errors,
    onItemsChange,
    onTouched,
    addFallback,
    removeLabel,
  }: GroupFieldProps) => {
    const { language } = useContext(StoreContext).TranslationsState
    const min = groupMinItems(question)
    const max = groupMaxItems(question)
    const addLabel = resolveLabel(question.addItemLabel, language) || addFallback
    const groupError = errors[question.id]

    const nextKeyRef = useRef(items.length)
    const [itemKeys, setItemKeys] = useState<string[]>(() => items.map((_, i) => `item-${i}`))

    const setInner = (index: number, innerId: string, value: ScalarAnswerValue) => {
      onItemsChange(items.map((item, i) => (i === index ? { ...item, [innerId]: value } : item)))
      onTouched(groupFieldErrorKey(question.id, index, innerId))
    }

    const addItem = () => {
      onItemsChange([...items, buildItemInitialValues(question.questions)])
      setItemKeys((prev) => [...prev, `item-${nextKeyRef.current++}`])
      onTouched(question.id)
    }

    const removeItem = (index: number) => {
      onItemsChange(items.filter((_, i) => i !== index))
      setItemKeys((prev) => prev.filter((_, i) => i !== index))
      onTouched(question.id)
    }

    return (
      <div className="data-collection__group">
        <div className="data-collection__label">{resolveLabel(question.label, language)}</div>

        {items.map((item, index) => (
          <div className="data-collection__group-item" key={itemKeys[index] ?? `item-${index}`}>
            {
              <div className="data-collection__group-button">
                <button
                  type="button"
                  className="button--secondary icon-button"
                  aria-label={removeLabel}
                  onClick={() => removeItem(index)}
                >
                  <Icon type="trash" size="24px" />
                </button>
              </div>
            }
            {question.questions
              .filter((inner) => isQuestionVisible(inner, item))
              .map((inner) => (
                <ScalarQuestionField
                  key={inner.id}
                  question={inner}
                  value={item[inner.id]}
                  error={errors[groupFieldErrorKey(question.id, index, inner.id)]}
                  onChange={(value) => setInner(index, inner.id, value)}
                  onBlur={() => onTouched(groupFieldErrorKey(question.id, index, inner.id))}
                  idPrefix={`${question.id}.${index}.`}
                />
              ))}
          </div>
        ))}

        {groupError && <span className="data-collection__error">{groupError}</span>}

        {items.length < max && (
          <div className="data-collection__group-button">
            <Button
              onClick={addItem}
              className="button--secondary"
              dataTestid="data-collection-add-item"
            >
              <Icon type="plus" size="24px" />
              {addLabel}
            </Button>
          </div>
        )}
      </div>
    )
  }
)
